/**
 * PluginLoadingUI — Blue Steel loading surface for dynamic plugin mount.
 *
 * - Manifest Lucide icon, step-aware subtitles, slow-load guidance
 * - Smoothed progress (partial credit for the active step)
 * - In-place error state with Retry + Back to home
 * - Motion tokens + prefers-reduced-motion
 * - Playwright-ready data-testid hooks
 */

import React from 'react';
import { Check, X, Loader2, RotateCcw, Home } from 'lucide-react';
import { ExperienceIcon } from '../core/icons/ExperienceIcon';
import { shellLoadingHostStyle } from '../core/layout/shellLoadingHost';

export interface LoadingStep {
	id: string;
	label: string;
	status: 'pending' | 'loading' | 'complete' | 'error';
}

export interface PluginLoadingUIProps {
	pluginId: string;
	pluginName: string;
	steps: LoadingStep[];
	/** Lucide registry key from plugin manifest (e.g. "tower-control"). */
	iconKey?: string | null;
	errorMessage?: string;
	onRetry?: () => void;
	onBack?: () => void;
}

const SLOW_LOAD_MS = 8_000;

const STEP_SUBTITLES: Record<string, (pluginName: string) => string> = {
	sdk: () => 'Loading shared authorization and navigation tools…',
	manifest: (name) => `Reading ${name} configuration…`,
	bundle: (name) => `Downloading the ${name} app bundle…`,
	routes: (name) => `Preparing ${name} screens and menus…`,
	authorization: () => 'Confirming which features you can open…',
};

function computeProgress(steps: LoadingStep[]): number {
	if (steps.length === 0) return 0;
	const completed = steps.filter((s) => s.status === 'complete').length;
	const loadingIndex = steps.findIndex((s) => s.status === 'loading');
	const errorIndex = steps.findIndex((s) => s.status === 'error');

	if (errorIndex >= 0) {
		return Math.max(5, (errorIndex / steps.length) * 100);
	}
	if (loadingIndex >= 0) {
		return ((completed + 0.45) / steps.length) * 100;
	}
	return (completed / steps.length) * 100;
}

function resolveSubtitle(
	steps: LoadingStep[],
	pluginName: string,
	slowLoad: boolean,
	hasError: boolean,
): string {
	if (hasError) {
		return 'Something went wrong while opening this app. You can retry or return home.';
	}

	const active =
		steps.find((s) => s.status === 'loading') ??
		steps.find((s) => s.status === 'pending');

	if (active && STEP_SUBTITLES[active.id]) {
		const base = STEP_SUBTITLES[active.id](pluginName);
		if (slowLoad && active.id === 'bundle') {
			return `${base} This is taking longer than usual — large bundles or a slow network can cause delays.`;
		}
		if (slowLoad) {
			return `${base} Still working — thank you for waiting.`;
		}
		return base;
	}

	return `Getting ${pluginName} ready…`;
}

const durFast = 'var(--dur-fast, 150ms)';
const durBase = 'var(--dur-base, 240ms)';
const durSlow = 'var(--dur-slow, 400ms)';
const easeStandard = 'var(--ease-standard, cubic-bezier(0.4, 0, 0.2, 1))';
const easeBounce = 'var(--ease-bounce, cubic-bezier(0.34, 1.56, 0.64, 1))';

export function PluginLoadingUI({
	pluginId,
	pluginName,
	steps,
	iconKey,
	errorMessage,
	onRetry,
	onBack,
}: PluginLoadingUIProps) {
	const [visible, setVisible] = React.useState(false);
	const [slowLoad, setSlowLoad] = React.useState(false);
	const [elapsedSec, setElapsedSec] = React.useState(0);
	const mountRef = React.useRef(Date.now());

	const hasError = steps.some((s) => s.status === 'error') || Boolean(errorMessage);
	const progress = computeProgress(steps);
	const subtitle = resolveSubtitle(steps, pluginName, slowLoad, hasError);

	React.useEffect(() => {
		requestAnimationFrame(() => setVisible(true));
		mountRef.current = Date.now();
	}, [pluginId]);

	React.useEffect(() => {
		if (hasError) return undefined;

		const slowTimer = window.setTimeout(() => setSlowLoad(true), SLOW_LOAD_MS);
		const tick = window.setInterval(() => {
			setElapsedSec(Math.floor((Date.now() - mountRef.current) / 1000));
		}, 1000);

		return () => {
			window.clearTimeout(slowTimer);
			window.clearInterval(tick);
		};
	}, [pluginId, hasError]);

	const title = hasError ? `Couldn't open ${pluginName}` : `Loading ${pluginName}`;

	return (
		<div data-shell-loading-host style={shellLoadingHostStyle()}>
		<div
			style={{
				display: 'flex',
				flexDirection: 'column',
				alignItems: 'center',
				justifyContent: 'center',
				width: '100%',
				maxWidth: '560px',
				minHeight: '400px',
				padding: '48px 24px',
				background:
					'linear-gradient(135deg, color-mix(in srgb, var(--quantum-violet, var(--main-accent)) 5%, transparent) 0%, color-mix(in srgb, var(--quantum-violet, var(--main-accent)) 8%, transparent) 100%)',
				borderRadius: '16px',
				margin: 0,
				position: 'relative',
				overflow: 'hidden',
				boxSizing: 'border-box',
				opacity: visible ? 1 : 0,
				transform: visible ? 'scale(1) translateY(0)' : 'scale(0.97) translateY(20px)',
				transition: `opacity ${durSlow} ${easeBounce}, transform ${durSlow} ${easeBounce}`,
			}}
			role={hasError ? 'alert' : 'status'}
			aria-live={hasError ? 'assertive' : 'polite'}
			aria-label={title}
			data-testid="shell-plugin-loading"
			data-plugin-loading=""
		>
			<div
				aria-hidden="true"
				style={{
					position: 'absolute',
					top: '-30%',
					left: '50%',
					transform: 'translateX(-50%)',
					width: '80%',
					height: '60%',
					background:
						'radial-gradient(ellipse, color-mix(in srgb, var(--quantum-violet, var(--main-accent)) 15%, transparent), transparent 70%)',
					pointerEvents: 'none',
					animation: hasError ? 'none' : 'pluginAmbientPulse 3s ease-in-out infinite',
				}}
			/>

			<div
				style={{
					position: 'relative',
					marginBottom: '20px',
					opacity: visible ? 1 : 0,
					transform: visible ? 'scale(1)' : 'scale(0.5)',
					transition: `opacity ${durSlow} ${easeBounce} 100ms, transform ${durSlow} ${easeBounce} 100ms`,
				}}
			>
				<div
					aria-hidden="true"
					style={{
						position: 'absolute',
						inset: '-16px',
						borderRadius: '50%',
						background:
							'radial-gradient(circle, color-mix(in srgb, var(--quantum-violet, var(--main-accent)) 25%, transparent), transparent 70%)',
						animation: hasError ? 'none' : 'pluginGlowPulse 2s ease-in-out infinite',
					}}
				/>
				<div
					aria-hidden="true"
					style={{
						position: 'absolute',
						inset: '-10px',
						borderRadius: '50%',
						border: '2px dashed color-mix(in srgb, var(--quantum-violet, var(--main-accent)) 25%, transparent)',
						animation: hasError ? 'none' : 'pluginOrbitSpin 8s linear infinite',
					}}
				>
					<div
						style={{
							position: 'absolute',
							top: '-4px',
							left: '50%',
							transform: 'translateX(-50%)',
							width: '8px',
							height: '8px',
							borderRadius: '50%',
							backgroundColor: 'var(--quantum-violet, var(--main-accent))',
							boxShadow: '0 0 10px var(--quantum-violet, var(--main-accent))',
						}}
					/>
				</div>
				<div
					data-testid="shell-plugin-loading-icon"
					style={{
						color: hasError
							? 'var(--error-color, var(--danger-color, #ef4444))'
							: 'var(--quantum-violet, var(--main-accent))',
						lineHeight: 0,
						animation: hasError ? 'none' : 'pluginIconFloat 2s ease-in-out infinite',
					}}
				>
					<ExperienceIcon iconKey={iconKey ?? 'app-window'} size={44} strokeWidth={1.5} />
				</div>
			</div>

			<h2
				data-testid="shell-plugin-loading-title"
				style={{
					margin: '0 0 8px 0',
					fontSize: '24px',
					fontWeight: '600',
					color: 'var(--main-text)',
					textAlign: 'center',
					opacity: visible ? 1 : 0,
					transform: visible ? 'translateY(0)' : 'translateY(10px)',
					transition: `opacity ${durSlow} ${easeStandard} 150ms, transform ${durSlow} ${easeStandard} 150ms`,
				}}
			>
				{title}
			</h2>

			<p
				data-testid="shell-plugin-loading-subtitle"
				style={{
					margin: '0 0 8px 0',
					fontSize: '14px',
					color: 'var(--secondary-text, color-mix(in srgb, var(--main-text) 60%, transparent))',
					textAlign: 'center',
					maxWidth: '420px',
					lineHeight: 1.5,
					opacity: visible ? 1 : 0,
					transform: visible ? 'translateY(0)' : 'translateY(10px)',
					transition: `opacity ${durSlow} ${easeStandard} 200ms, transform ${durSlow} ${easeStandard} 200ms`,
				}}
			>
				{subtitle}
			</p>

			{!hasError && elapsedSec >= 3 && (
				<p
					data-testid="shell-plugin-loading-elapsed"
					style={{
						margin: '0 0 24px 0',
						fontSize: '12px',
						color: 'var(--secondary-text, color-mix(in srgb, var(--main-text) 50%, transparent))',
					}}
				>
					{elapsedSec}s elapsed
				</p>
			)}
			{hasError && <div style={{ marginBottom: '24px' }} />}

			{hasError && errorMessage && (
				<p
					data-testid="shell-plugin-loading-error-detail"
					style={{
						margin: '0 0 24px 0',
						fontSize: '13px',
						color: 'var(--error-color, var(--danger-color, #ef4444))',
						textAlign: 'center',
						maxWidth: '420px',
						lineHeight: 1.45,
					}}
				>
					{errorMessage}
				</p>
			)}

			<div
				data-testid="shell-plugin-loading-progress"
				aria-valuenow={Math.round(progress)}
				aria-valuemin={0}
				aria-valuemax={100}
				role="progressbar"
				aria-label={`Loading progress for ${pluginName}`}
				style={{
					width: '100%',
					maxWidth: '400px',
					height: '10px',
					backgroundColor: 'var(--card-border, color-mix(in srgb, var(--main-text) 15%, transparent))',
					borderRadius: '5px',
					overflow: 'hidden',
					marginBottom: '32px',
					position: 'relative',
					opacity: visible ? 1 : 0,
					transition: `opacity ${durSlow} ${easeStandard} 250ms`,
				}}
			>
				<div
					style={{
						width: `${progress}%`,
						height: '100%',
						background: hasError
							? 'var(--error-color, var(--danger-color, #ef4444))'
							: 'linear-gradient(90deg, var(--quantum-violet, var(--main-accent)), color-mix(in srgb, var(--quantum-violet, var(--main-accent)) 70%, white))',
						borderRadius: '5px',
						transition: `width ${durBase} ${easeStandard}`,
						position: 'relative',
						overflow: 'hidden',
						boxShadow: hasError
							? 'none'
							: '0 0 15px color-mix(in srgb, var(--quantum-violet, var(--main-accent)) 50%, transparent)',
					}}
				>
					{!hasError && (
						<div
							aria-hidden="true"
							style={{
								position: 'absolute',
								top: 0,
								left: 0,
								right: 0,
								bottom: 0,
								background:
									'linear-gradient(90deg, transparent 0%, color-mix(in srgb, white 40%, transparent) 50%, transparent 100%)',
								animation: 'pluginShimmer 1.5s infinite',
							}}
						/>
					)}
				</div>
			</div>

			<div
				data-testid="shell-plugin-loading-steps"
				style={{
					width: '100%',
					maxWidth: '400px',
					display: 'flex',
					flexDirection: 'column',
					gap: '12px',
					opacity: visible ? 1 : 0,
					transition: `opacity ${durSlow} ${easeStandard} 300ms`,
				}}
			>
				{steps.map((step, index) => {
					const isComplete = step.status === 'complete';
					const isLoading = step.status === 'loading';
					const isError = step.status === 'error';

					return (
						<div
							key={step.id}
							data-testid={`shell-plugin-loading-step-${step.id}`}
							style={{
								display: 'flex',
								alignItems: 'center',
								gap: '12px',
								padding: '12px 16px',
								backgroundColor: isError
									? 'color-mix(in srgb, var(--error-color, var(--danger-color, #ef4444)) 10%, transparent)'
									: isLoading
										? 'color-mix(in srgb, var(--quantum-violet, var(--main-accent)) 12%, transparent)'
										: 'transparent',
								borderRadius: '8px',
								transition: `background-color ${durFast} ${easeStandard}, transform ${durFast} ${easeStandard}, opacity ${durFast} ${easeStandard}`,
								opacity: step.status === 'pending' ? 0.5 : 1,
								transform: isLoading ? 'translateX(4px)' : 'translateX(0)',
								borderLeft: isError
									? '3px solid var(--error-color, var(--danger-color, #ef4444))'
									: isLoading
										? '3px solid var(--quantum-violet, var(--main-accent))'
										: '3px solid transparent',
							}}
						>
							<div
								style={{
									width: '26px',
									height: '26px',
									borderRadius: '50%',
									display: 'flex',
									alignItems: 'center',
									justifyContent: 'center',
									fontSize: '12px',
									fontWeight: '600',
									backgroundColor: isError
										? 'var(--error-color, var(--danger-color, #ef4444))'
										: isComplete
											? 'var(--success-color, #10b981)'
											: isLoading
												? 'var(--quantum-violet, var(--main-accent))'
												: 'var(--card-border, color-mix(in srgb, var(--main-text) 20%, transparent))',
									color:
										isComplete || isError || isLoading
											? 'var(--color-on-accent, white)'
											: 'var(--secondary-text, color-mix(in srgb, var(--main-text) 60%, transparent))',
									transition: `background-color ${durFast} ${easeStandard}, box-shadow ${durFast} ${easeStandard}`,
									boxShadow:
										isComplete || isLoading
											? `0 0 10px ${isComplete ? 'var(--success-color, #10b981)' : 'var(--quantum-violet, var(--main-accent))'}`
											: 'none',
								}}
							>
								{isError ? (
									<X size={15} strokeWidth={2.5} aria-hidden="true" />
								) : isComplete ? (
									<Check size={15} strokeWidth={2.5} aria-hidden="true" />
								) : isLoading ? (
									<Loader2
										size={15}
										strokeWidth={2.5}
										aria-hidden="true"
										style={{ animation: 'pluginLoaderSpin 0.9s linear infinite' }}
									/>
								) : (
									index + 1
								)}
							</div>

							<span
								style={{
									flex: 1,
									fontSize: '14px',
									color: isError
										? 'var(--error-color, var(--danger-color, #ef4444))'
										: isComplete
											? 'var(--success-color, #10b981)'
											: isLoading
												? 'var(--main-text)'
												: 'var(--secondary-text, color-mix(in srgb, var(--main-text) 60%, transparent))',
									fontWeight: isLoading || isError ? '600' : '400',
									transition: `color ${durFast} ${easeStandard}`,
								}}
							>
								{step.label}
							</span>

							{isLoading && (
								<div aria-hidden="true" style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
									{[0, 1, 2].map((i) => (
										<span
											key={i}
											style={{
												width: '6px',
												height: '6px',
												borderRadius: '50%',
												backgroundColor: 'var(--quantum-violet, var(--main-accent))',
												animation: `pluginDotBounce 1.4s infinite ${i * 0.2}s`,
											}}
										/>
									))}
								</div>
							)}
						</div>
					);
				})}
			</div>

			{hasError && (onRetry || onBack) && (
				<div
					data-testid="shell-plugin-loading-actions"
					style={{
						display: 'flex',
						gap: '12px',
						marginTop: '28px',
						flexWrap: 'wrap',
						justifyContent: 'center',
					}}
				>
					{onRetry && (
						<button
							type="button"
							data-testid="shell-plugin-loading-retry"
							onClick={onRetry}
							style={{
								display: 'inline-flex',
								alignItems: 'center',
								gap: '8px',
								padding: '10px 18px',
								borderRadius: '8px',
								border: 'none',
								cursor: 'pointer',
								fontWeight: 600,
								fontSize: '14px',
								color: 'var(--color-on-accent, white)',
								background: 'var(--button-primary-bg, var(--quantum-violet, var(--main-accent)))',
								transition: `opacity ${durFast} ${easeStandard}, transform ${durFast} ${easeStandard}`,
							}}
						>
							<RotateCcw size={16} aria-hidden="true" />
							Try again
						</button>
					)}
					{onBack && (
						<button
							type="button"
							data-testid="shell-plugin-loading-back"
							onClick={onBack}
							style={{
								display: 'inline-flex',
								alignItems: 'center',
								gap: '8px',
								padding: '10px 18px',
								borderRadius: '8px',
								border: '1px solid var(--card-border, color-mix(in srgb, var(--main-text) 20%, transparent))',
								cursor: 'pointer',
								fontWeight: 600,
								fontSize: '14px',
								color: 'var(--main-text)',
								background: 'transparent',
								transition: `background-color ${durFast} ${easeStandard}`,
							}}
						>
							<Home size={16} aria-hidden="true" />
							Back to home
						</button>
					)}
				</div>
			)}

			<style>{`
				@keyframes pluginIconFloat {
					0%, 100% { transform: translateY(0); }
					50% { transform: translateY(-6px); }
				}
				@keyframes pluginGlowPulse {
					0%, 100% { opacity: 0.5; transform: scale(1); }
					50% { opacity: 1; transform: scale(1.1); }
				}
				@keyframes pluginOrbitSpin {
					0% { transform: rotate(0deg); }
					100% { transform: rotate(360deg); }
				}
				@keyframes pluginAmbientPulse {
					0%, 100% { opacity: 0.5; }
					50% { opacity: 0.8; }
				}
				@keyframes pluginShimmer {
					0% { transform: translateX(-100%); }
					100% { transform: translateX(200%); }
				}
				@keyframes pluginLoaderSpin {
					from { transform: rotate(0deg); }
					to { transform: rotate(360deg); }
				}
				@keyframes pluginDotBounce {
					0%, 80%, 100% { transform: translateY(0); opacity: 0.5; }
					40% { transform: translateY(-6px); opacity: 1; }
				}
				@media (prefers-reduced-motion: reduce) {
					[data-plugin-loading],
					[data-plugin-loading] * {
						animation: none !important;
						transition: none !important;
					}
				}
			`}</style>
		</div>
		</div>
	);
}
